import { useEffect, useMemo, useRef } from 'react';

import { useTimeZone } from '@/app/preferences';
import { Timestamp } from '@/components/Timestamp';
import { formatClockZone, formatDuration } from '@/domain/time';

import type { TimelineItem } from './timeline';

const HATCH = 'repeating-linear-gradient(135deg, var(--status-none) 0 2px, transparent 2px 6px)';

/**
 * One mark per loaded run, oldest on the left and newest on the right, so the streak the
 * evaluator is counting can be read at a glance. Gaps and refused payloads keep their place in
 * time. Every mark carries its outcome in text, so the colours are a convenience, not the message.
 * The two ends are labelled with their times, because the table below runs the other way.
 */
export function OutcomeStrip({
  items,
  onSelect,
}: {
  items: TimelineItem[];
  onSelect: (runKey: string) => void;
}) {
  const tz = useTimeZone();
  const scroller = useRef<HTMLDivElement>(null);
  const oldestFirst = useMemo(() => [...items].reverse(), [items]);
  const runs = useMemo(() => oldestFirst.filter((i) => i.kind === 'run'), [oldestFirst]);
  const oldest = runs[0];
  const newest = runs[runs.length - 1];

  // Newest is what matters; start scrolled to the right and stay there as older pages load.
  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [oldestFirst.length]);

  return (
    <div className="flex flex-col gap-2">
      <div
        ref={scroller}
        className="overflow-x-auto scrollbar-thin"
        role="group"
        aria-label="Outcome of each loaded run, oldest on the left, newest on the right"
      >
        <div className="flex min-w-max items-end gap-[3px] px-0.5 py-1">
          {oldestFirst.map((item) => {
            if (item.kind === 'run') {
              const outcome = item.run.judged.outcome;
              const when = formatClockZone(item.run.completed_at, tz);
              return (
                <button
                  key={item.run.run_key}
                  type="button"
                  onClick={() => onSelect(item.run.run_key)}
                  aria-label={`${outcome} at ${when}`}
                  title={`${outcome} at ${when}${item.run.judged.driving_check ? ` · ${item.run.judged.driving_check}` : ''}`}
                  className={[
                    'h-6 w-3 shrink-0 rounded-[2px] transition-tone hover:opacity-75',
                    outcome === 'PASS' ? 'bg-status-ok' : 'bg-status-failed',
                  ].join(' ')}
                />
              );
            }
            if (item.kind === 'gap') {
              const { window } = item;
              const label = window.closed_at
                ? `Blind gap: ${formatDuration(Date.parse(window.closed_at) - Date.parse(window.opened_at))}, ${window.missed_runs_at_close ?? '?'} runs missed`
                : `Blind since ${formatClockZone(window.opened_at, tz)}`;
              return (
                <span
                  key={`gap-${window.opened_at}`}
                  role="img"
                  aria-label={label}
                  title={label}
                  className="h-6 w-[30px] shrink-0 rounded-[2px] bg-status-none-soft"
                  style={{ backgroundImage: HATCH }}
                />
              );
            }
            const label = `Refused payload at ${formatClockZone(item.entry.received_at, tz)}: ${item.entry.reason.replaceAll('_', ' ')}`;
            return (
              <span
                key={item.entry.quarantine_id}
                role="img"
                aria-label={label}
                title={label}
                className="h-6 w-3 shrink-0 rounded-[2px] border border-status-impaired bg-status-impaired-soft"
              />
            );
          })}
        </div>
      </div>
      {oldest?.kind === 'run' && newest?.kind === 'run' ? (
        <div className="flex flex-wrap justify-between gap-x-4 text-meta text-ink-3">
          <span>
            Oldest loaded · <Timestamp iso={oldest.run.completed_at} mode="auto" />
          </span>
          <span>
            Newest · <Timestamp iso={newest.run.completed_at} mode="auto" />
          </span>
        </div>
      ) : null}
      <ul className="m-0 flex list-none flex-wrap gap-x-4 gap-y-1 p-0 text-meta text-ink-2">
        <li className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-[12px] w-[8px] rounded-[2px] bg-status-ok"
            aria-hidden="true"
          />{' '}
          Pass
        </li>
        <li className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-[12px] w-[8px] rounded-[2px] bg-status-failed"
            aria-hidden="true"
          />{' '}
          Fail
        </li>
        <li className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-[12px] w-[18px] rounded-[2px] bg-status-none-soft"
            style={{ backgroundImage: HATCH }}
            aria-hidden="true"
          />{' '}
          Blind gap
        </li>
        <li className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-[12px] w-[8px] rounded-[2px] border border-status-impaired bg-status-impaired-soft"
            aria-hidden="true"
          />{' '}
          Refused payload
        </li>
      </ul>
    </div>
  );
}
